import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { db, storage } from "@lib/firebase";
import {
  collection,
  addDoc,
  getDoc,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
  deleteDoc,
  limit,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "../common/NotificationContext";
import { jsPDF } from "jspdf";
import { InputFieldRace } from "../common/InputFieldRace";
import { VoucherInput } from "../common/VoucherInput";
import { StepIndicator } from "../common/StepIndicator";
import { InputField, Button } from "@cakrawalajakarta/shared-components";
import { serverTimestamp } from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/free-solid-svg-icons";
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from "react-google-recaptcha-v3";

const createEmptyRelayMember = () => ({
  namaLengkap: "",
  bibName: "",
  telepon: "",
  nomorIdentitas: "",
  tanggalLahir: "",
  golonganDarah: "",
  sizeJersey: "",
  gender: "",
  negara: "Indonesia",
  provinsi: "",
  kota: "",
  alamat: "",
  kontakDarurat: "",
  namaKontakDarurat: "",
});

const relayRequiredFields = [
  ["namaLengkap", "Nama lengkap"],
  ["bibName", "BIB Name"],
  ["telepon", "Nomor telepon"],
  ["nomorIdentitas", "Nomor identitas"],
  ["tanggalLahir", "Tanggal lahir"],
  ["golonganDarah", "Golongan darah"],
  ["sizeJersey", "Ukuran jersey"],
  ["gender", "Jenis kelamin"],
  ["negara", "Negara"],
  ["alamat", "Alamat lengkap"],
  ["kontakDarurat", "Nomor kontak darurat"],
  ["namaKontakDarurat", "Nama kontak darurat"],
  ["provinsi", "Provinsi"],
];

const sanitizeRelayMember = (member = {}) =>
  Object.keys(createEmptyRelayMember()).reduce((acc, key) => {
    acc[key] = typeof member[key] === "string" ? member[key].trim().replace(/\s+/g, " ") : member[key] || "";
    if (key === "nomorIdentitas" && typeof member[key] === "string") {
      acc[key] = member[key].trim().replace(/\s+/g, "");
    }
    return acc;
  }, {});

const FormPendaftaranContent = ({ labelColor = "text-slate-800" }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { title, price, foreignerPrice, eventId } = location.state || {};
  const { user, getIdToken } = useAuth();
  const { showNotification } = useNotification();
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [formData, setFormData] = useState({
    namaDepan: "",
    namaBelakang: "",
    namaLengkap: "",
    email: "",
    telepon: "",
    nomorIdentitas: "",
    tanggalLahir: "",
    gender: "",
    negara: "Indonesia",
    provinsi: "",
    kota: "",
    alamat: "",
    kontakDarurat: "",
    namaKontakDarurat: "",
    hubunganKontakDarurat: "",
    bibName: "",
    golonganDarah: "",
    sizeJersey: "",
    performanceIndex: "",
    raceResultLink: "",
    certificateUrl: "",
    riwayatMedis: "",
    teamName: "",
    relayMembers: [
      createEmptyRelayMember(),
      createEmptyRelayMember(),
      createEmptyRelayMember(),
      createEmptyRelayMember(),
    ],
    participants: [
      {
        namaDepan: "",
        namaBelakang: "",
        bibName: "",
        telepon: "",
        nomorIdentitas: "",
        tanggalLahir: "",
        golonganDarah: "",
        sizeJersey: "",
        gender: "",
        negara: "Indonesia",
        provinsi: "",
        kota: "",
        alamat: "",
        kontakDarurat: "",
        namaKontakDarurat: "",
        hubunganKontakDarurat: "",
        performanceIndex: "",
        raceResultLink: "",
      },
      {
        namaDepan: "",
        namaBelakang: "",
        bibName: "",
        telepon: "",
        nomorIdentitas: "",
        tanggalLahir: "",
        golonganDarah: "",
        sizeJersey: "",
        gender: "",
        negara: "Indonesia",
        provinsi: "",
        kota: "",
        alamat: "",
        kontakDarurat: "",
        namaKontakDarurat: "",
        hubunganKontakDarurat: "",
        performanceIndex: "",
        raceResultLink: "",
      },
      {
        namaDepan: "",
        namaBelakang: "",
        bibName: "",
        telepon: "",
        nomorIdentitas: "",
        tanggalLahir: "",
        golonganDarah: "",
        sizeJersey: "",
        gender: "",
        negara: "Indonesia",
        provinsi: "",
        kota: "",
        alamat: "",
        kontakDarurat: "",
        namaKontakDarurat: "",
        hubunganKontakDarurat: "",
        performanceIndex: "",
        raceResultLink: "",
      },
    ],
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [isSnapReady, setIsSnapReady] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [transactionStatus, setTransactionStatus] = useState(null);
  const [snapToken, setSnapToken] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [voucherCode, setVoucherCode] = useState(null);
  const [discount, setDiscount] = useState(0);
  const [finalPrice, setFinalPrice] = useState(price || 0);
  const [collectionName, setCollectionName] = useState(null);
  const [categoryName, setCategoryName] = useState(null);
  const [distance, setDistance] = useState(null);
  const [isDraftLoaded, setIsDraftLoaded] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);

  const STEPS = ["Participant Info", "Waiver & Review", "Submission"];

  const isGroupRegistration = false; // Ultra24 doesn't use JOTR-style group registration
  const isUltraCategory = categoryName ? !categoryName.toLowerCase().includes("fun run") : true;
  const isRelayCategory = categoryName ? categoryName.toLowerCase().includes("relay") : false;
  const isFunRunCategory = categoryName ? categoryName.toLowerCase().includes("fun run") : false;

  // Qualification tier for ultra categories
  const getQualificationRequirement = () => {
    if (!categoryName || isFunRunCategory) return null;
    const cn = categoryName.toLowerCase();
    if (cn.includes("100k") || cn.includes("12h") || cn.includes("24h")) {
      return "FM"; // Full Marathon
    }
    return "HM"; // Half Marathon (for FM/42K, 50K, 6H, Relay)
  };
  const qualificationRequirement = getQualificationRequirement();

  const handleNextStep = () => {
    // Basic validation before moving to next step
    if (currentStep === 1) {
       if (!formData.namaLengkap || !formData.email || !formData.telepon) {
           showNotification("error", "Harap lengkapi data diri utama.");
           return;
       }

       // Validate ultra-category specific fields
       const requiredFields = [
         { field: "bibName", label: "Bib Name" },
         { field: "golonganDarah", label: "Golongan Darah" },
         { field: "sizeJersey", label: "Ukuran Jersey" },
       ];
       if (isUltraCategory) {
         requiredFields.push({ field: "raceResultLink", label: "Activity STRAVA atau Race Result" });
       }
       const missingField = requiredFields.find((item) => !formData[item.field]);
       if (missingField) {
         showNotification("error", `Harap lengkapi ${missingField.label} sebelum melanjutkan.`);
         return;
       }

       // Validate relay members
       if (isRelayCategory) {
         const relayMembers = formData.relayMembers || [];
         const invalidMember = relayMembers.findIndex((member) =>
           relayRequiredFields.some(([field]) => !member[field])
         );
         if (invalidMember !== -1) {
           showNotification("error", `Harap lengkapi seluruh biodata Member ${invalidMember + 2}.`);
           return;
         }
       }
    }
    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length));
    window.scrollTo(0, 0);
  };

  const handlePrevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const checkStep1Validity = () => {
    // 1. Common mandatory fields for the primary registrant
    const baseFields = [
      "namaLengkap",
      "email",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "provinsi",
      "kota",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
    ];

    const missingBaseField = baseFields.find((field) => !formData[field]);
    if (missingBaseField) return false;

    // 2. Ultra-specific fields
    if (isUltraCategory) {
      const requiredUltraFields = [
        "bibName",
        "golonganDarah",
        "sizeJersey",
        "raceResultLink",
      ];
      const missingUltraField = requiredUltraFields.find((field) => !formData[field]);
      if (missingUltraField) return false;
    } else {
      // Fun run still needs bib, blood type, jersey
      const requiredFunRunFields = ["bibName", "golonganDarah", "sizeJersey"];
      const missingFunRunField = requiredFunRunFields.find((field) => !formData[field]);
      if (missingFunRunField) return false;
    }

    // 3. Relay member validation
    if (isRelayCategory) {
      const relayValid = (formData.relayMembers || []).every((m) =>
        relayRequiredFields.every(([field]) => m[field])
      );
      if (!relayValid) return false;
    }

    return true;
  };

  // Update finalPrice berdasarkan negara (peserta utama atau anggota tim)
  useEffect(() => {
    if (price && foreignerPrice) {
      let basePrice;
      if (isGroupRegistration) {
        const hasForeignParticipant =
          formData.negara !== "Indonesia" ||
          formData.participants.some(
            (participant) => participant.negara !== "Indonesia"
          );
        basePrice = hasForeignParticipant ? foreignerPrice : price;
      } else {
        basePrice = formData.negara === "Indonesia" ? price : foreignerPrice;
      }
      const newFinalPrice = Math.floor(basePrice * (1 - discount / 100));
      setFinalPrice(newFinalPrice);
    }
  }, [
    formData.negara,
    formData.participants,
    price,
    foreignerPrice,
    discount,
    isGroupRegistration,
  ]);

  const humanize = (str) => {
    if (!str) return "";
    if (str === "nomorIdentitas") return "Nomor Identitas";
    const withSpaces = str.replace(/([A-Z])/g, " $1").trim();
    return withSpaces
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const fetchCollectionName = async (eventId) => {
    try {
      // 1. Get Configuration (Collection Name) from 'events'
      // This is the source of truth for "dynamic mapping" per user instruction
      const configRef = doc(db, "events", eventId);
      const configDoc = await getDoc(configRef);
      // STRICT MAPPING: Must exist in 'events' collection. No fallbacks.
      const validCollectionName = configDoc.exists() ? configDoc.data().collectionName : null;
      setCollectionName(validCollectionName);

      // 2. Get Event Content (Title, Price, Categories) from 'semuaKegiatan'
      // This refers to the actual event display data
      const contentRef = doc(db, "semuaKegiatan", eventId);
      const contentDoc = await getDoc(contentRef);
      
      if (contentDoc.exists()) {
          return contentDoc.data();
      }
      
      throw new Error("Dokumen event tidak ditemukan.");
    } catch (error) {
      setErrorMsg("Gagal mengambil data event. Silakan coba lagi.");
      showNotification(
        "error",
        "Gagal mengambil data event. Silakan coba lagi."
      );
      return null;
    }
  };

  const fetchCategoryDetails = async (eventId, title) => {
    try {
      const eventRef = doc(db, "semuaKegiatan", eventId);
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const eventData = eventDoc.data();
        if (eventData.type === "seminar") {
          setCategoryName(eventData.title);
          setDistance(0);
        } else {
          const categories = eventData.categories || [];
          let selectedCategory = categories.find((cat) =>
            title.includes(cat.categoryName)
          );
          if (!selectedCategory && categories.length > 0) {
            selectedCategory = categories.find(
              (cat) => cat.categoryName === "Vertical 7K"
            );
          }
          if (selectedCategory) {
            setCategoryName(selectedCategory.categoryName);
            setDistance(selectedCategory.distance);
          } else {
            throw new Error("Kategori tidak ditemukan di data event.");
          }
        }
      } else {
        throw new Error("Dokumen event tidak ditemukan.");
      }
    } catch (error) {
      setErrorMsg("Gagal memuat detail kategori. Silakan coba lagi.");
      showNotification(
        "error",
        "Gagal memuat detail kategori. Silakan coba lagi."
      );
    }
  };

  const checkRegistration = async (collectionName) => {
    // Ensure all required parameters are available before querying
    if (!user || !eventId || !collectionName || !categoryName) {
      return;
    }

    try {
      const q = query(
        collection(db, collectionName),
        where("uid", "==", user.uid),
        where("eventId", "==", eventId),
        where("kategori", "==", categoryName),
        limit(1)
      );
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        const data = doc.data();
        const status = data.transaction_status || "unknown";
        const paymentType = data.payment_type || "unknown";
        const harga = data.harga || 0;

        if (
          (status === "settlement" &&
            (paymentType === "free" || harga === 0)) ||
          (status === "pending" && paymentType !== "free") ||
          (status === "settlement" && paymentType !== "free" && data.snapToken)
        ) {
          setRegistrationStatus("registered");
          setTransactionStatus(status);
          setSnapToken(data.snapToken || null);
          setOrderId(data.orderId || null);
          setVoucherCode(data.voucherCode || null);
          setFormData((prev) => ({
            ...prev,
            teamName: isGroupRegistration ? data.teamName || prev.teamName : "",
          }));
        } else if (
          ["expire", "cancel", "deny", "failure"].includes(status)
        ) {
          // If expired/cancelled/failed, delete the old registration so user can re-register
          await deleteDoc(doc.ref);
          setRegistrationStatus("not_registered");
          setTransactionStatus(null);
          setSnapToken(null);
          setOrderId(null);
          setVoucherCode(null);
        }
      } else {
        setRegistrationStatus("not_registered");
      }
    } catch (err) {
      // Don't block form for registration check failure
      // User can still proceed with registration
      console.error("[FormPendaftaran] Failed to check registration:", err);
      setRegistrationStatus("not_registered");
    }
  };

  const loadDraftData = async () => {
    if (!user || !eventId || !isGroupRegistration || isDraftLoaded) return;

    try {
      const draftDocRef = doc(db, "registrations", `${user.uid}_${eventId}`);
      const draftDoc = await getDoc(draftDocRef);
      if (draftDoc.exists()) {
        const draftData = draftDoc.data();
        if (draftData.status === "draft") {
          const updatedParticipants = draftData.participants.map(
            (participant, index) => ({
              namaDepan: participant.namaDepan || "",
              namaBelakang: participant.namaBelakang || "",
              bibName: participant.bibName || "",
              telepon: participant.telepon || "",
              nomorIdentitas: participant.nomorIdentitas || "",
              tanggalLahir: participant.tanggalLahir || "",
              golonganDarah: participant.golonganDarah || "",
              sizeJersey: participant.sizeJersey || "",
              gender: participant.gender || "",
              negara: participant.negara || "Indonesia",
              provinsi: participant.provinsi || "",
              kota: participant.kota || "",
              alamat: participant.alamat || "",
              kontakDarurat: participant.kontakDarurat || "",
              namaKontakDarurat: participant.namaKontakDarurat || "",
              hubunganKontakDarurat: participant.hubunganKontakDarurat || "",
              performanceIndex: participant.performanceIndex || "",
              raceResultLink: participant.raceResultLink || "",
            })
          );
          setFormData((prev) => {
            const firstParticipant = updatedParticipants[0] || {};
            const newFormData = {
              ...prev,
              teamName: draftData.teamName || "",
              participants: updatedParticipants,
              namaDepan: firstParticipant.namaDepan || prev.namaDepan,
              namaBelakang: firstParticipant.namaBelakang || prev.namaBelakang,
              namaLengkap:
                firstParticipant.namaDepan && firstParticipant.namaBelakang
                  ? `${firstParticipant.namaDepan} ${firstParticipant.namaBelakang}`.trim()
                  : prev.namaLengkap,
              telepon: firstParticipant.telepon || prev.telepon,
              nomorIdentitas:
                firstParticipant.nomorIdentitas || prev.nomorIdentitas,
              tanggalLahir: firstParticipant.tanggalLahir || prev.tanggalLahir,
              gender: firstParticipant.gender || prev.gender,
              negara: firstParticipant.negara || prev.negara,
              provinsi: firstParticipant.provinsi || prev.provinsi,
              kota: firstParticipant.kota || prev.kota,
              alamat: firstParticipant.alamat || prev.alamat,
              kontakDarurat:
                firstParticipant.kontakDarurat || prev.kontakDarurat,
              namaKontakDarurat:
                firstParticipant.namaKontakDarurat || prev.namaKontakDarurat,
              hubunganKontakDarurat:
                firstParticipant.hubunganKontakDarurat ||
                prev.hubunganKontakDarurat,
              bibName: firstParticipant.bibName || prev.bibName,
              golonganDarah:
                firstParticipant.golonganDarah || prev.golonganDarah,
              sizeJersey: firstParticipant.sizeJersey || prev.sizeJersey,
              performanceIndex:
                firstParticipant.performanceIndex || prev.performanceIndex,
              raceResultLink:
                firstParticipant.raceResultLink || prev.raceResultLink,
            };
            return newFormData;
          });
          showNotification(
            "success",
            "Data draft sebelumnya telah dimuat. Silakan lanjutkan pengisian atau submit."
          );
          setIsDraftLoaded(true);
        }
      } else {
        setIsDraftLoaded(true);
      }
    } catch (err) {
      setErrorMsg("Gagal memuat data draft. Silakan coba lagi.");
      showNotification("error", "Gagal memuat data draft. Silakan coba lagi.");
      setIsDraftLoaded(true);
    }
  };

  useEffect(() => {
    if (!eventId || !title || !price || !foreignerPrice) {
      showNotification(
        "error",
        "Silakan pilih kategori dari halaman kegiatan."
      );
      navigate("/");
      return;
    }

    if (!Number.isInteger(price) || !Number.isInteger(foreignerPrice)) {
      setErrorMsg("Harga event tidak valid.");
      showNotification("error", "Harga event tidak valid.");
      navigate("/");
      return;
    }

    const initialize = async () => {
      if (isInitialized) return;
      
      if (!user) {
        navigate("/login");
        showNotification("error", "Silakan login untuk mendaftar.");
        return;
      }

      try {
        const eventData = await fetchCollectionName(eventId);
        if (!eventData) {
          navigate("/");
          return;
        }
        await fetchCategoryDetails(eventId, title);

        if (isGroupRegistration && !isDraftLoaded) {
          await loadDraftData();
        }

        if (!isDraftLoaded || !isGroupRegistration) {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          let userData = {};

          if (docSnap.exists()) {
            userData = docSnap.data();
            let namaDepan = userData.namaDepan || "";
            let namaBelakang = userData.namaBelakang || "";
            let namaLengkap = userData.namaLengkap || "";

            if (!namaDepan && !namaBelakang && namaLengkap) {
              const namaSplit = namaLengkap.trim().split(/\s+/);
              namaDepan = namaSplit[0] || "";
              namaBelakang = namaSplit.slice(1).join(" ") || "";
            }

            let participants = formData.participants;
            let rootFields = {};
            if (isGroupRegistration) {
              participants = [
                {
                  namaDepan,
                  namaBelakang,
                  bibName: userData.bibName || "",
                  telepon: userData.telepon || "",
                  nomorIdentitas: userData.nomorIdentitas || "",
                  tanggalLahir: userData.tanggalLahir || "",
                  golonganDarah: userData.golonganDarah || "",
                  sizeJersey: userData.sizeJersey || "",
                  gender: userData.gender || "",
                  negara: userData.negara || "Indonesia",
                  provinsi: userData.provinsi || "",
                  kota: userData.kota || "",
                  alamat: userData.alamat || "",
                  kontakDarurat: userData.kontakDarurat || "",
                  namaKontakDarurat: userData.namaKontakDarurat || "",
                  hubunganKontakDarurat: userData.hubunganKontakDarurat || "",
                  performanceIndex: userData.performanceIndex || "",
                  raceResultLink: userData.raceResultLink || "",
                },
                {
                  namaDepan: "",
                  namaBelakang: "",
                  bibName: "",
                  telepon: "",
                  nomorIdentitas: "",
                  tanggalLahir: "",
                  golonganDarah: "",
                  sizeJersey: "",
                  gender: "",
                  negara: "Indonesia",
                  provinsi: "",
                  kota: "",
                  alamat: "",
                  kontakDarurat: "",
                  namaKontakDarurat: "",
                  hubunganKontakDarurat: "",
                  performanceIndex: "",
                  raceResultLink: "",
                },
                {
                  namaDepan: "",
                  namaBelakang: "",
                  bibName: "",
                  telepon: "",
                  nomorIdentitas: "",
                  tanggalLahir: "",
                  golonganDarah: "",
                  sizeJersey: "",
                  gender: "",
                  negara: "Indonesia",
                  provinsi: "",
                  kota: "",
                  alamat: "",
                  kontakDarurat: "",
                  namaKontakDarurat: "",
                  hubunganKontakDarurat: "",
                  performanceIndex: "",
                  raceResultLink: "",
                },
              ];
              rootFields = {
                namaDepan,
                namaBelakang,
                namaLengkap:
                  namaLengkap || `${namaDepan} ${namaBelakang}`.trim(),
                email: userData.email || user.email || "",
                telepon: userData.telepon || "",
                nomorIdentitas: userData.nomorIdentitas || "",
                tanggalLahir: userData.tanggalLahir || "",
                gender: userData.gender || "",
                negara: userData.negara || "Indonesia",
                provinsi: userData.provinsi || "",
                kota: userData.kota || "",
                alamat: userData.alamat || "",
                kontakDarurat: userData.kontakDarurat || "",
                namaKontakDarurat: userData.namaKontakDarurat || "",
                hubunganKontakDarurat: userData.hubunganKontakDarurat || "",
                bibName: "",
                golonganDarah: "",
                sizeJersey: "",
                performanceIndex: "",
                raceResultLink: "",
                certificateUrl: "",
                riwayatMedis: "",
              };
            } else {
              rootFields = {
                namaDepan,
                namaBelakang,
                namaLengkap:
                  namaLengkap || `${namaDepan} ${namaBelakang}`.trim(),
                email: userData.email || user.email || "",
                telepon: userData.telepon || "",
                nomorIdentitas: userData.nomorIdentitas || "",
                tanggalLahir: userData.tanggalLahir || "",
                gender: userData.gender || "",
                negara: userData.negara || "Indonesia",
                provinsi: userData.provinsi || "",
                kota: userData.kota || "",
                alamat: userData.alamat || "",
                kontakDarurat: userData.kontakDarurat || "",
                namaKontakDarurat: userData.namaKontakDarurat || "",
                hubunganKontakDarurat: userData.hubunganKontakDarurat || "",
                bibName: "",
                golonganDarah: "",
                sizeJersey: "",
                performanceIndex: "",
                raceResultLink: "",
                certificateUrl: "",
                riwayatMedis: "",
              };
            }

            setFormData((prev) => {
              const newFormData = {
                ...prev,
                ...rootFields,
                teamName: isGroupRegistration ? prev.teamName : "",
                participants: isGroupRegistration
                  ? participants
                  : prev.participants,
              };
              return newFormData;
            });
          } else {
            const namaSplit = (user.displayName || "").trim().split(/\s+/);
            const namaDepan = namaSplit[0] || "";
            const namaBelakang = namaSplit.slice(1).join(" ") || "";
            setFormData((prev) => {
              const newFormData = {
                ...prev,
                namaDepan,
                namaBelakang,
                namaLengkap:
                  namaDepan && namaBelakang
                    ? `${namaDepan} ${namaBelakang}`.trim()
                    : "",
                email: user.email || "",
              };
              return newFormData;
            });
          }
        }


        
        setIsInitialized(true);

        // checkRegistration sekarang ditangani oleh useEffect terpisah
        // untuk mencegah race condition dan error permission
      } catch (err) {
        setErrorMsg("Gagal mengambil data pengguna.");
        showNotification("error", "Gagal mengambil data pengguna.");
        return;
      }
    };



    const checkSnap = setInterval(() => {
      if (window.snap) {
        setIsSnapReady(true);
        clearInterval(checkSnap);
      }
    }, 100);

    // Initial Data Fetching
    if (eventId && title && price && user) {
      initialize();
    } else {
      showNotification(
        "error",
        "Silakan pilih kategori dari halaman kegiatan."
      );
      navigate("/");
    }

    return () => clearInterval(checkSnap);
  }, [
    eventId,
    title,
    price,
    foreignerPrice,
    navigate,
    showNotification,
    user,
    // Removed collectionName and categoryName from here to prevent re-running initialize loop
  ]);

  // Separate Effect for Checking Registration
  useEffect(() => {
    // Only run if we have all necessary pieces of information
    if (user && eventId && collectionName && categoryName && !registrationStatus) {
      console.log("[FormPendaftaran] Checking registration for:", {
        collection: collectionName,
        category: categoryName
      });
      checkRegistration(collectionName);
    }
  }, [user, eventId, collectionName, categoryName, registrationStatus]);

  const handleChange = (e, participantIndex = null) => {
    if (participantIndex !== null) {
      const { name, value } = e.target;
      setFormData((prev) => {
        const updatedParticipants = [...prev.participants];
        updatedParticipants[participantIndex] = {
          ...updatedParticipants[participantIndex],
          [name]: value,
        };
        const updatedFormData = { ...prev, participants: updatedParticipants };
        return updatedFormData;
      });
    } else {
      const { name, value } = e.target;
      setFormData((prev) => {
        const updated = { ...prev, [name]: value };
        if (name === "namaDepan" || name === "namaBelakang") {
          updated.namaLengkap = `${updated.namaDepan || ""} ${
            updated.namaBelakang || ""
          }`.trim();
        }
        if (name === "namaLengkap") {
          const namaSplit = value.trim().split(/\s+/);
          updated.namaDepan = namaSplit[0] || "";
          updated.namaBelakang = namaSplit.slice(1).join(" ") || "";
        }
        return updated;
      });
    }
  };

  const handleRelayMemberChange = (index, field, value) => {
    setFormData((prev) => {
      const updatedRelayMembers = [...(prev.relayMembers || [])];
      updatedRelayMembers[index] = {
        ...updatedRelayMembers[index],
        [field]: value,
      };
      return { ...prev, relayMembers: updatedRelayMembers };
    });
  };

  const handleApplyVoucher = (code, discountPercentage) => {
    setVoucherCode(code);
    setDiscount(discountPercentage);
    let basePrice;
    if (isGroupRegistration) {
      const hasForeignParticipant =
        formData.negara !== "Indonesia" ||
        formData.participants.some(
          (participant) => participant.negara !== "Indonesia"
        );
      basePrice = hasForeignParticipant ? foreignerPrice : price;
    } else {
      basePrice = formData.negara === "Indonesia" ? price : foreignerPrice;
    }
    const newFinalPrice = Math.floor(
      basePrice * (1 - discountPercentage / 100)
    );
    setFinalPrice(newFinalPrice);
  };

  const updateUserData = async (updatedFields) => {
    try {
      const docRef = doc(db, "users", user.uid);
      const docSnap = await getDoc(docRef);
      const existingData = docSnap.exists() ? docSnap.data() : {};

      const fieldsToUpdate = {};
      Object.keys(updatedFields).forEach((key) => {
        if (
          key !== "participants" &&
          key !== "teamName" &&
          updatedFields[key] !== undefined &&
          updatedFields[key] !== ""
        ) {
          fieldsToUpdate[key] = updatedFields[key];
        } else if (!existingData[key]) {
          fieldsToUpdate[key] = "";
        }
      });
      fieldsToUpdate.updatedAt = serverTimestamp();

      await updateDoc(docRef, fieldsToUpdate);
    } catch (err) {
      setErrorMsg("Gagal memperbarui data pengguna.");
      showNotification("error", "Gagal memperbarui data pengguna.");
    }
  };

  const simpanSuratPernyataanSebagaiPDF = async () => {
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const marginLeft = 20;
    const marginTop = 20;
    const lineHeight = 7;
    const bulletIndent = 5;
    let yPosition = marginTop;

    // Header
    pdf.setFontSize(16);
    pdf.setFont("helvetica", "bold");
    pdf.text("SURAT PERNYATAAN", 105, yPosition, { align: "center" });
    yPosition += lineHeight * 2;

    pdf.setFontSize(12);
    pdf.setFont("helvetica", "normal");

    // Informasi peserta
    const namaLengkap = formData.namaLengkap || "Unknown";
    const today = new Date();
    const tanggal = today.toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });

    pdf.text(`Nama: ${namaLengkap}`, marginLeft, yPosition);
    yPosition += lineHeight;
    if (isGroupRegistration) {
      pdf.text(
        `Nama Tim: ${formData.teamName || "Unknown"}`,
        marginLeft,
        yPosition
      );
      yPosition += lineHeight;
    }
    pdf.text(`Tanggal: ${tanggal}`, marginLeft, yPosition);
    yPosition += lineHeight * 2;

    // Isi surat
    const paragraphs = [
      `Dengan ini menyatakan bahwa saya secara sukarela dan sadar mendaftarkan diri${
        isGroupRegistration ? " dan tim saya" : ""
      } sebagai peserta dalam kegiatan ULTRA24 yang diselenggarakan oleh Cakrawala Jakarta Kreatif.`,
      "Saya memahami bahwa kegiatan ini bersifat trail run yang menantang secara fisik dan mental, serta memiliki potensi risiko, seperti kondisi cuaca ekstrem, kontur medan yang berat, dan gangguan kesehatan lainnya. Oleh karena itu, saya menyatakan bahwa saya telah mempersiapkan diri secara optimal untuk mengikuti lomba ini dan dalam kondisi kesehatan yang baik.",
      `Saya juga menyadari bahwa panitia penyelenggara, relawan, sponsor, maupun pihak-pihak terkait lainnya tidak bertanggung jawab secara hukum atas risiko cedera, kecelakaan, kehilangan, atau kejadian darurat lainnya yang mungkin ${
        isGroupRegistration ? "saya atau anggota tim saya" : "saya"
      } alami selama mengikuti rangkaian kegiatan. Seluruh tanggung jawab atas keselamatan dan kesehatan pribadi selama kegiatan merupakan tanggung jawab ${
        isGroupRegistration ? "saya dan anggota tim saya" : "saya sendiri"
      }, dan saya melepaskan panitia dari segala bentuk tuntutan hukum atas kejadian yang mungkin timbul selama atau akibat keikutsertaan ${
        isGroupRegistration ? "saya dan tim saya" : "saya"
      } dalam kegiatan ini.`,
      "Saya berkomitmen untuk:",
      [
        "Mengikuti seluruh peraturan dan arahan dari panitia dan pihak berwenang.",
        "Menjaga sportivitas, keamanan, dan ketertiban selama kegiatan berlangsung.",
        "Tidak melakukan tindakan yang merugikan diri sendiri, peserta lain, panitia, maupun lingkungan sekitar.",
      ],
      `Saya juga memberikan izin penuh kepada panitia untuk menggunakan dokumentasi (foto, video, atau lainnya) yang memuat identitas atau citra ${
        isGroupRegistration ? "saya dan anggota tim saya" : "saya"
      } untuk keperluan publikasi, promosi, dan dokumentasi kegiatan tanpa memerlukan izin tambahan.`,
      `Dengan ini, saya menyatakan telah membaca, memahami, dan menyetujui seluruh isi pernyataan ini dengan sadar, sukarela, dan tanpa tekanan dari pihak mana pun. Pernyataan ini saya buat sebagai bentuk komitmen dan tanggung jawab ${
        isGroupRegistration ? "saya dan tim saya" : "saya"
      } sebagai peserta dalam ULTRA24.`,
    ];

    paragraphs.forEach((item) => {
      if (Array.isArray(item)) {
        item.forEach((bullet) => {
          const lines = pdf.splitTextToSize(bullet, 170 - bulletIndent);
          lines.forEach((line, index) => {
            if (yPosition + lineHeight > 277) {
              pdf.addPage();
              yPosition = marginTop;
            }
            const text = index === 0 ? `• ${line}` : line;
            const xOffset =
              index === 0 ? marginLeft : marginLeft + bulletIndent;
            pdf.text(text, xOffset, yPosition);
            yPosition += lineHeight;
          });
        });
      } else {
        const lines = pdf.splitTextToSize(item, 170);
        lines.forEach((line) => {
          if (yPosition + lineHeight > 277) {
            pdf.addPage();
            yPosition = marginTop;
          }
          pdf.text(line, marginLeft, yPosition);
          yPosition += lineHeight;
        });
      }
      yPosition += lineHeight;
    });

    if (yPosition + lineHeight * 3 > 277) {
      pdf.addPage();
      yPosition = marginTop;
    }
    pdf.text("Hormat saya,", 170, yPosition, { align: "right" });
    yPosition += lineHeight * 2;
    pdf.setFont("helvetica", "bold");
    pdf.text(namaLengkap, 170, yPosition, { align: "right" });

    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.text(`Halaman ${i} dari ${pageCount}`, 105, 287, { align: "center" });
    }

    const pdfBlob = pdf.output("blob");
    const fileName = `Surat_Pernyataan_${namaLengkap.replace(
      /\s+/g,
      "_"
    )}_${eventId}${
      isGroupRegistration ? `_${formData.teamName.replace(/\s+/g, "_")}` : ""
    }.pdf`;

    try {
      console.log(`🔄 Mengunggah surat pernyataan untuk user ${user.uid}...`);
      const suratQuery = query(
        collection(db, "suratPernyataan"),
        where("userId", "==", user.uid),
        where("eventId", "==", eventId),
        limit(1)
      );
      const suratSnapshot = await getDocs(suratQuery);
      if (!suratSnapshot.empty) {
        console.log(
          `⚠️ Surat pernyataan untuk user ${user.uid} sudah ada, melewati...`
        );
        return;
      }

      const storageRef = ref(
        storage,
        `suratPernyataan/${user.uid}/${fileName}`
      );
      await uploadBytes(storageRef, pdfBlob);
      const downloadURL = await getDownloadURL(storageRef);
      console.log(
        `✅ File ${fileName} berhasil diunggah untuk user ${user.uid}`
      );

      const docData = {
        namaLengkap,
        tanggal,
        fileName,
        downloadURL,
        createdAt: serverTimestamp(),
        userId: user.uid,
        eventId,
      };
      if (isGroupRegistration) {
        docData.teamName = formData.teamName || "Unknown";
      }

      await addDoc(collection(db, "suratPernyataan"), docData);
      console.log(
        `✅ Metadata surat pernyataan disimpan untuk user ${user.uid}`
      );
    } catch (err) {
      console.error(
        `❌ Gagal menyimpan surat pernyataan untuk user ${user.uid}:`,
        err.message
      );
      setErrorMsg("Gagal menyimpan surat pernyataan.");
      showNotification("error", "Gagal menyimpan surat pernyataan.");
      throw err;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission to avoid duplicate Order IDs
    if (loading) return;
    
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!isSnapReady) {
      setErrorMsg("Midtrans sedang disiapkan, mohon tunggu sebentar.");
      showNotification(
        "error",
        "Midtrans sedang disiapkan, mohon tunggu sebentar."
      );
      setLoading(false);
      return;
    }

    if (!isChecked) {
      setErrorMsg("Harap setujui Surat Pernyataan sebelum melanjutkan.");
      showNotification(
        "error",
        "Harap setujui Surat Pernyataan sebelum melanjutkan."
      );
      setLoading(false);
      return;
    }

    if (!categoryName || (eventId !== "PelatihanPelatih" && !distance)) {
      setErrorMsg("Data kategori atau jarak tidak lengkap.");
      showNotification("error", "Data kategori atau jarak tidak lengkap.");
      setLoading(false);
      return;
    }

    const validateFormat = (
      field,
      value,
      participantIndex = null,
      negara = "Indonesia"
    ) => {
      const prefix =
        participantIndex !== null ? `Peserta ${participantIndex + 1}: ` : "";
      const label = humanize(field);

      if (!value || value.trim() === "") {
        return `${prefix}${label} wajib diisi`;
      }
      if (field === "teamName" && !/^[A-Za-z0-9\s]{3,50}$/.test(value)) {
        return `${prefix}${label} harus 3-50 karakter, alfanumerik dengan spasi.`;
      }
      if (
        (field === "namaDepan" || field === "namaBelakang") &&
        !/^[A-Za-z\s]{1,50}$/.test(value)
      ) {
        return `${prefix}${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
      }
      if (field === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return `${prefix}${label} harus dalam format yang valid (contoh: user@example.com).`;
      }
      if (field === "bibName" && !/^[A-Za-z0-9\s]{1,20}$/.test(value)) {
        return `${prefix}${label} harus 1-20 karakter alfanumerik dan spasi.`;
      }
      if (
        (field === "telepon" || field === "kontakDarurat") &&
        !/^\+?\d{8,15}$/.test(value)
      ) {
        return `${prefix}${label} harus 8-15 digit, boleh diawali dengan "+".`;
      }
      if (field === "nomorIdentitas") {
        if (negara === "Indonesia") {
          if (!/^\d{16}$/.test(value)) {
            return `${prefix}${label} (KTP) harus 16 digit untuk peserta dari Indonesia.`;
          }
        } else {
          if (!/^[A-Za-z0-9]{5,20}$/.test(value)) {
            return `${prefix}${label} (Paspor/Identitas) harus 5-20 karakter alfanumerik untuk peserta asing.`;
          }
        }
      }
      if (field === "tanggalLahir") {
        const date = new Date(value);
        const eventDate = new Date("2026-07-24");
        const minAgeDate = new Date(
          eventDate.getFullYear() - 17,
          eventDate.getMonth(),
          eventDate.getDate()
        );
        if (isNaN(date.getTime()) || date >= eventDate) {
          return `${prefix}${label} harus di masa lalu sebelum 24/07/2026`;
        } else if (date > minAgeDate) {
          return `${prefix}Peserta harus berusia minimal 17 tahun pada 24/07/2026`;
        }
      }
      if (field === "golonganDarah" && !["A", "B", "AB", "O"].includes(value)) {
        return `${prefix}${label} harus A, B, AB, atau O.`;
      }
      if (
        field === "sizeJersey" &&
        !["XS", "S", "M", "L", "XL", "XXL"].includes(value)
      ) {
        return `${prefix}${label} harus XS, S, M, L, XL, atau XXL.`;
      }
      if (field === "gender" && !["Laki-laki", "Perempuan"].includes(value)) {
        return `${prefix}${label} harus Laki-laki atau Perempuan.`;
      }
      if (field === "alamat" && !/^[A-Za-z0-9\s,.-/#()]{5,100}$/.test(value)) {
        return `${prefix}${label} harus 5-100 karakter, boleh huruf, angka, spasi, koma, titik, tanda hubung, garis miring, pagar, atau tanda kurung.`;
      }
      if (field === "namaKontakDarurat" && !/^[A-Za-z\s]{1,50}$/.test(value)) {
        return `${prefix}${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
      }
      if (
        field === "hubunganKontakDarurat" &&
        !/^[A-Za-z\s]{1,50}$/.test(value)
      ) {
        return `${prefix}${label} hanya boleh huruf dan spasi, maksimum 50 karakter.`;
      }
      if (field === "performanceIndex" && value && !/^\d{3}$/.test(value)) {
        return `${prefix}${label} harus berupa 3 digit angka, isi "000" jika tidak ada index`;
      }
      if (field === "raceResultLink") {
        if (!value || !value.trim()) {
           return `${prefix} atau applikasi sejenis wajib diisi`;
        }
        // Strict URL check
        const urlRegex = /^(https?:\/\/[^\s]+)/;
        if (!urlRegex.test(value)) {
            return `${prefix}Link tidak valid. Pastikan diawali dengan https://`;
        }
      }
      return "";
    };

    const requiredFields = [
      "namaDepan",
      "namaBelakang",
      "email",
      "telepon",
      "nomorIdentitas",
      "tanggalLahir",
      "gender",
      "negara",
      "alamat",
      "kontakDarurat",
      "namaKontakDarurat",
      "hubunganKontakDarurat",
      "bibName",
      "golonganDarah",
      "sizeJersey",
      ...(isUltraCategory ? ["raceResultLink"] : []),
    ];

    const errors = {};
    for (const field of requiredFields) {
      const error = validateFormat(
        field,
        formData[field],
        null,
        formData.negara
      );
      if (error) {
        errors[field] = error;
      }
    }

    // Relay member validation
    if (isRelayCategory) {
      const relayMembers = formData.relayMembers || [];
      if (relayMembers.length !== 4) {
        errors.relayMembers = "Relay team harus memiliki tepat 4 anggota tambahan.";
      } else {
        for (let i = 0; i < relayMembers.length; i++) {
          const member = relayMembers[i] || {};
          for (const [field, label] of relayRequiredFields) {
            if (!member[field] || !String(member[field]).trim()) {
              errors[`relayMember_${field}_${i}`] = `${label} anggota ${i + 2} wajib diisi.`;
            }
          }
        }
      }
    }

    if (Object.keys(errors).length > 0) {
      const firstError = Object.values(errors)[0];
      setErrorMsg(firstError);
      showNotification("error", firstError);
      setFormData((prev) => ({ ...prev, errors }));
      setLoading(false);
      return;
    }

    if (!Number.isInteger(finalPrice)) {
      setErrorMsg("Harga akhir harus berupa bilangan bulat.");
      showNotification("error", "Harga akhir harus berupa bilangan bulat.");
      setLoading(false);
      return;
    }

    if (!executeRecaptcha) {
      showNotification("error", "Recaptcha belum siap. Silakan refresh halaman.");
      setLoading(false);
      return;
    }

    const recaptchaToken = await executeRecaptcha("registration_submit");
    if (!recaptchaToken) {
        showNotification("error", "Gagal memverifikasi captcha (bot terdeteksi).");
        setLoading(false);
        return;
    }

    try {
      if (!eventId || !title || !price || !foreignerPrice) {
        throw new Error(
          "Data event tidak lengkap. Silakan coba lagi dari halaman kategori."
        );
      }

      const sanitizeValue = (value) => {
        if (typeof value === "string") {
          const trimmed = value.trim();
          return trimmed === "" ? undefined : trimmed.replace(/\s+/g, " ");
        }
        return value;
      };

      const sanitizedFormData = {
        ...formData,
        namaDepan: sanitizeValue(formData.namaDepan),
        namaBelakang: sanitizeValue(formData.namaBelakang),
        namaLengkap: sanitizeValue(formData.namaLengkap),
        email: sanitizeValue(formData.email),
        telepon: sanitizeValue(formData.telepon),
        nomorIdentitas: formData.nomorIdentitas
          ? formData.nomorIdentitas.trim().replace(/\s+/g, "")
          : undefined,
        tanggalLahir: sanitizeValue(formData.tanggalLahir),
        gender: sanitizeValue(formData.gender),
        negara: sanitizeValue(formData.negara),
        provinsi: sanitizeValue(formData.provinsi),
        kota: sanitizeValue(formData.kota),
        alamat: sanitizeValue(formData.alamat),
        kontakDarurat: sanitizeValue(formData.kontakDarurat),
        namaKontakDarurat: sanitizeValue(formData.namaKontakDarurat),
        hubunganKontakDarurat: sanitizeValue(formData.hubunganKontakDarurat),
        bibName: sanitizeValue(formData.bibName),
        golonganDarah: sanitizeValue(formData.golonganDarah),
        sizeJersey: sanitizeValue(formData.sizeJersey),
        performanceIndex: sanitizeValue(formData.performanceIndex),
        raceResultLink: isUltraCategory ? sanitizeValue(formData.raceResultLink) : undefined,
        certificateUrl: isUltraCategory ? sanitizeValue(formData.certificateUrl) : undefined,
        riwayatMedis: sanitizeValue(formData.riwayatMedis),
        relayMembers: isRelayCategory
          ? (formData.relayMembers || []).map(sanitizeRelayMember)
          : undefined,
      };

      const sanitizedErrors = {};
      for (const field of requiredFields) {
        const error = validateFormat(
          field,
          sanitizedFormData[field],
          null,
          sanitizedFormData.negara
        );
        if (error) {
          sanitizedErrors[field] = error;
        }
      }
      // Re-validate relay members on sanitized data
      if (isRelayCategory && sanitizedFormData.relayMembers) {
        for (let i = 0; i < sanitizedFormData.relayMembers.length; i++) {
          const m = sanitizedFormData.relayMembers[i];
          for (const [field, label] of relayRequiredFields) {
            if (!m[field]) sanitizedErrors[`relayMember_${field}_${i}`] = `${label} anggota ${i + 2} wajib diisi.`;
          }
        }
      }

      if (Object.keys(sanitizedErrors).length > 0) {
        const firstError = Object.values(sanitizedErrors)[0];
        setErrorMsg(firstError);
        showNotification("error", firstError);
        setFormData((prev) => ({ ...prev, errors: sanitizedErrors }));
        setLoading(false);
        return;
      }

      const updatedUserData = {
        namaDepan: sanitizedFormData.namaDepan,
        namaBelakang: sanitizedFormData.namaBelakang,
        namaLengkap: sanitizedFormData.namaLengkap,
        telepon: sanitizedFormData.telepon,
        nomorIdentitas: sanitizedFormData.nomorIdentitas,
        tanggalLahir: sanitizedFormData.tanggalLahir,
        gender: sanitizedFormData.gender,
        negara: sanitizedFormData.negara,
        provinsi: sanitizedFormData.provinsi,
        kota: sanitizedFormData.kota,
        alamat: sanitizedFormData.alamat,
        kontakDarurat: sanitizedFormData.kontakDarurat,
        namaKontakDarurat: sanitizedFormData.namaKontakDarurat,
        hubunganKontakDarurat: sanitizedFormData.hubunganKontakDarurat,
        bibName: sanitizedFormData.bibName,
        golonganDarah: sanitizedFormData.golonganDarah,
        sizeJersey: sanitizedFormData.sizeJersey,
        performanceIndex: sanitizedFormData.performanceIndex,
        raceResultLink: isUltraCategory ? sanitizedFormData.raceResultLink : undefined,
        certificateUrl: isUltraCategory ? sanitizedFormData.certificateUrl : undefined,
        riwayatMedis: sanitizedFormData.riwayatMedis,
      };

      await updateUserData(updatedUserData);

      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        throw new Error(
          "Data pengguna tidak ditemukan di Firestore setelah pembaruan."
        );
      }
      const userData = userDoc.data();

      const requiredUserFields = [
        "namaDepan",
        "namaBelakang",
        "bibName",
        "nomorIdentitas",
        "tanggalLahir",
        "golonganDarah",
        "sizeJersey",
        "gender",
        "kontakDarurat",
        "namaKontakDarurat",
        "hubunganKontakDarurat",
        "negara",
        ...(isUltraCategory ? ["raceResultLink"] : []),
      ];
      const missingUserFields = requiredUserFields.filter(
        (field) => !userData[field] || userData[field].trim() === ""
      );
      if (missingUserFields.length > 0) {
        throw new Error(
          `Data pengguna tidak lengkap: ${missingUserFields.join(
            ", "
          )} wajib diisi.`
        );
      }

      await simpanSuratPernyataanSebagaiPDF();

      const payload = {
        namaDepan: sanitizedFormData.namaDepan,
        namaBelakang: sanitizedFormData.namaBelakang,
        namaLengkap: sanitizedFormData.namaLengkap,
        email: sanitizedFormData.email,
        telepon: sanitizedFormData.telepon,
        nomorIdentitas: sanitizedFormData.nomorIdentitas,
        tanggalLahir: sanitizedFormData.tanggalLahir,
        gender: sanitizedFormData.gender,
        negara: sanitizedFormData.negara,
        provinsi: sanitizedFormData.provinsi,
        kota: sanitizedFormData.kota,
        alamat: sanitizedFormData.alamat,
        kontakDarurat: sanitizedFormData.kontakDarurat,
        namaKontakDarurat: sanitizedFormData.namaKontakDarurat,
        hubunganKontakDarurat: sanitizedFormData.hubunganKontakDarurat,
        bibName: sanitizedFormData.bibName,
        golonganDarah: sanitizedFormData.golonganDarah,
        sizeJersey: sanitizedFormData.sizeJersey,
        performanceIndex: sanitizedFormData.performanceIndex || undefined,
        raceResultLink: isUltraCategory ? sanitizedFormData.raceResultLink : undefined,
        certificateUrl: isUltraCategory ? sanitizedFormData.certificateUrl : undefined,
        riwayatMedis: sanitizedFormData.riwayatMedis || undefined,
        kategori: categoryName,
        distance: distance || 0,
        harga: Math.floor(finalPrice),
        eventId,
        voucherCode,
        uid: user.uid,
        collectionName,
        relayMembers: isRelayCategory ? sanitizedFormData.relayMembers : undefined,
        recaptchaToken,
      };

      const token = await getIdToken();
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/register`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (finalPrice === 0) {
        setSuccessMsg("System hacked 🤯 Registration 100% free!");
        showNotification("success", "System hacked 🤯 Registration 100% free!");
        setOrderId(response.data.orderId);
        const draftDocRef = doc(db, "registrations", `${user.uid}_${eventId}`);
        await deleteDoc(draftDocRef);
        setTimeout(
          () =>
            navigate(
              `/myregistration?order_id=${response.data.orderId}&transaction_status=settlement`
            ),
          2000
        );
        return;
      }

      const { orderId } = response.data;
      setOrderId(orderId);

      // OLD FLOW: Trigger Snap automatically
      // window.snap.pay(snapToken, { ... });

      // NEW FLOW: Redirect to waiting for verification
      setSuccessMsg("Pendaftaran berhasil! Mohon tunggu verifikasi admin.");
      showNotification("success", "Pendaftaran berhasil! Mohon tunggu verifikasi admin.");
      
      const draftDocRef = doc(
        db,
        "registrations",
        `${user.uid}_${eventId}`
      );
      await deleteDoc(draftDocRef);

      setTimeout(() => {
        navigate(`/myregistration?order_id=${orderId}&status=pending_verification`);
      }, 2000);

      // Remove dependency on snapToken for this step
      setSnapToken(null);

    } catch (error) {
      let errorMessage = "Pendaftaran gagal. Silakan coba lagi.";
      let errorDetails = "";
      if (error.response?.data) {
        errorMessage = error.response.data.message || errorMessage;
        if (error.response.data.errors) {
          const errors = error.response.data.errors;
          if (errors.firstParticipant) {
            errorMessage =
              Object.values(errors.firstParticipant)[0] || errorMessage;
          } else if (errors.participant_1 || errors.participant_2) {
            errorMessage =
              Object.values(errors.participant_1 || errors.participant_2)[0] ||
              errorMessage;
          }
          errorDetails = JSON.stringify(errors, null, 2);
        }
      } else {
        errorMessage = error.message || errorMessage;
      }
      setErrorMsg(`${errorMessage}${errorDetails ? `: ${errorDetails}` : ""}`);
      showNotification(
        "error",
        `${errorMessage}${errorDetails ? `: ${errorDetails}` : ""}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleBayar = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!isSnapReady || !window.snap) {
      setErrorMsg("Midtrans belum siap. Silakan coba lagi.");
      showNotification("error", "Midtrans belum siap. Silakan coba lagi.");
      setLoading(false);
      return;
    }

    if (!snapToken || !orderId) {
      setErrorMsg("Token pembayaran atau order ID tidak ditemukan.");
      showNotification(
        "error",
        "Token pembayaran atau order ID tidak ditemukan."
      );
      setLoading(false);
      return;
    }

    try {
      window.snap.pay(snapToken, {
        onSuccess: async () => {
          setSuccessMsg("Pembayaran berhasil!");
          showNotification("success", "Pembayaran berhasil!");
          const draftDocRef = doc(
            db,
            "registrations",
            `${user.uid}_${eventId}`
          );
          await deleteDoc(draftDocRef);
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=settlement`
          );
        },
        onPending: async () => {
          setSuccessMsg("Pembayaran sedang diproses.");
          showNotification("warning", "Pembayaran sedang diproses.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=pending`
          );
        },
        onError: () => {
          setErrorMsg("Pembayaran gagal. Silakan coba lagi.");
          showNotification("error", "Pembayaran gagal. Silakan coba lagi.");
          navigate(
            `/myregistration?order_id=${orderId}&transaction_status=failed`
          );
        },
        onClose: () => {},
      });
    } catch (err) {
      setErrorMsg("Gagal memproses pembayaran. Silakan coba lagi.");
      showNotification("error", "Gagal memproses pembayaran.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isSettled = transactionStatus === "settlement";
  const isPending = transactionStatus === "pending";
  const isFormDisabled = isSettled || isPending;

  return (
    <div className="min-h-screen bg-white px-4 ">
      <div className="max-w-5xl mx-auto py-20">
        {/* Header */}
       

        {/* Form Container */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          {/* Title and Location */}
          <div className="mb-6">
            <h2 className="text-lg md:text-xl font-bold text-slate-800 uppercase">
              Jakarta Open Trail Run
            </h2>
            <div className="text-sm text-red-600 mt-1 flex items-center gap-1">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
              </svg>
              Cibodas, West Java - Indonesia
            </div>
          </div>
          {/* Step Indicator - Inside Card */}
          <div className="mb-4">
            <div className="relative flex items-center justify-between">
              {/* Connecting Line Background */}
              <div className="absolute top-4 left-0 right-0 mx-6 h-0.5 bg-gray-200" />
              {/* Connecting Line Progress */}
              <div
                className="absolute top-4 left-0 mx-6 h-0.5 bg-red-500 transition-all duration-500"
                style={{ width: `calc((100% - 3rem) * ${(currentStep - 1) / (STEPS.length - 1)})` }}
              />

              {/* Steps */}
              {STEPS.map((step, index) => {
                const stepNumber = index + 1;
                const isActive = stepNumber === currentStep;
                const isCompleted = stepNumber < currentStep;

                return (
                  <div
                    key={index}
                    className="flex flex-col relative items-center"
                  >
                    {/* Circle Wrapper - White background to mask the line locally */}
                    <div className="bg-white px-2 relative">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all duration-300 ${
                          isCompleted
                            ? "border-red-500 bg-red-500 text-white"
                            : isActive
                            ? "border-red-500 bg-white text-red-600"
                            : "border-gray-300 bg-white text-gray-400"
                        }`}
                      >
                        {isCompleted ? <FontAwesomeIcon icon={faCheck} className="text-xs" /> : stepNumber}
                      </div>
                    </div>
                    {/* Label - Outside the mask */}
                    <span
                      className={`text-xs font-medium mt-1 transition-colors text-center ${
                        isActive || isCompleted ? "text-red-600" : "text-gray-400"
                      }`}
                    >
                      {step}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Step 1: Participant Info */}
          {currentStep === 1 && (
            <>
              {/* Qualification Info Banner (ultra categories only) */}
              {isUltraCategory && qualificationRequirement && (
                <div className="mb-5 p-4 rounded-xl border border-amber-200 bg-amber-50">
                  <div className="flex items-start gap-3">
                    <span className="text-amber-600 text-lg mt-0.5">⚠️</span>
                    <div>
                      <h4 className="font-semibold text-amber-800 text-sm mb-1">
                        Minimum Qualification Requirement
                      </h4>
                      {qualificationRequirement === "FM" ? (
                        <p className="text-amber-700 text-sm leading-relaxed">
                          Participation in the <strong>{categoryName}</strong> category requires a minimum of one (1) official{" "}
                          <strong>Full Marathon (42K)</strong> race result. Participants must provide a valid Strava activity link or a finisher certificate as proof of qualification.
                        </p>
                      ) : (
                        <p className="text-amber-700 text-sm leading-relaxed">
                          Participation in the <strong>{categoryName}</strong> category requires a minimum of one (1) official{" "}
                          <strong>Half Marathon (21K)</strong> race result. Participants must provide a valid Strava activity link or a finisher certificate as proof of qualification.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h3 className="text-slate-800 font-semibold mb-1">Event Registration</h3>
                <p className="text-slate-600 text-sm">
                  You are registering for <strong>Indonesia ULTRA24 Jakarta</strong> in the <strong>{categoryName || title}</strong> category.
                </p>
              </div>
              
              <form id="form-pendaftaran" onSubmit={handleSubmit}>
                <InputFieldRace
                  formData={formData}
                  handleChange={handleChange}
                  isFormDisabled={isFormDisabled}
                  labelColor={labelColor}
                  eventId={eventId}
                  categoryName={categoryName}
                  updateUserData={updateUserData}
                />
              </form>

              {/* Relay Team Members (for Relay categories only) */}
              {isRelayCategory && (
                <div className="mt-8 p-5 rounded-xl border-2 border-red-100 bg-red-50">
                  <div className="mb-4">
                    <h3 className="text-red-800 font-bold text-base">Relay Team Members</h3>
                    <p className="text-red-600 text-sm mt-1">
                      You are registered as <strong>Member 1 (Team Leader)</strong>. Please provide the details of your 4 additional team members below.
                    </p>
                  </div>
                  <div className="space-y-4">
                    {(formData.relayMembers || []).map((member, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-red-100 shadow-sm">
                        <p className="text-xs font-bold text-red-500 uppercase tracking-wider mb-3">
                          Member {index + 2}
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {[
                            ["namaLengkap", "Full Name", "text", "Enter full name"],
                            ["bibName", "BIB Name", "text", "Name on BIB"],
                            ["telepon", "Phone Number", "text", "Active WhatsApp number"],
                            ["nomorIdentitas", "Identity Number (KTP/Passport)", "text", "Enter ID / passport number"],
                            ["tanggalLahir", "Date of Birth", "date", ""],
                            ["provinsi", "Province", "text", "Province / state"],
                            ["kota", "City", "text", "City"],
                            ["alamat", "Full Address", "text", "Full address"],
                            ["kontakDarurat", "Emergency Contact Number", "text", "Emergency phone"],
                            ["namaKontakDarurat", "Emergency Contact Name", "text", "Emergency contact name"],
                          ].map(([field, label, type, placeholder]) => (
                            <div key={field}>
                              <label className="block text-xs font-semibold text-slate-600 mb-1">
                                {label} <span className="text-red-500">*</span>
                              </label>
                              <input
                                type={type}
                                value={member[field] || ""}
                                onChange={(e) => handleRelayMemberChange(index, field, e.target.value)}
                                placeholder={placeholder}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition"
                                disabled={isFormDisabled}
                              />
                            </div>
                          ))}
                          {[
                            ["golonganDarah", "Blood Type", ["A", "B", "AB", "O"]],
                            ["sizeJersey", "Jersey Size", ["XS", "S", "M", "L", "XL", "XXL"]],
                            ["gender", "Gender", ["Laki-laki", "Perempuan"]],
                            ["negara", "Country", ["Indonesia"]],
                          ].map(([field, label, options]) => (
                            <div key={field}>
                              <label className="block text-xs font-semibold text-slate-600 mb-1">
                                {label} <span className="text-red-500">*</span>
                              </label>
                              <select
                                value={member[field] || ""}
                                onChange={(e) => handleRelayMemberChange(index, field, e.target.value)}
                                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-transparent transition bg-white"
                                disabled={isFormDisabled}
                              >
                                <option value="">Select...</option>
                                {options.map((option) => (
                                  <option key={option} value={option}>{option}</option>
                                ))}
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-8 border-t border-slate-200 mt-6">
                <Button
                  variant="ghost"
                  onClick={() => navigate("/")}
                  className="text-slate-600 hover:text-slate-800"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleNextStep}
                  disabled={!checkStep1Validity()}
                  variant="primary"
                >
                  Proceed to Waiver →
                </Button>
              </div>
            </>
          )}

          {/* Step 2: Waiver & Review */}
          {currentStep === 2 && (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-slate-800 text-center mb-4">
                  SURAT KESEPAKATAN PEMBEBASAN TANGGUNG JAWAB (WAIVER)
                </h2>
                <div className="bg-white rounded-xl border border-slate-200 p-4 max-h-96 overflow-y-auto text-sm text-slate-700 leading-relaxed">
                  <p className="mb-4">
                    Saya menyatakan telah membaca dan menyetujui keseluruhan pernyataan dalam dokumen ini, maka secara sadar Saya bertanggung jawab atas segala resiko yang terjadi selama dan setelah perlombaan berlangsung.
                  </p>
                  <p className="mb-4">
                    Sehubungan dengan kepesertaan Saya di <strong>ULTRA24 JAKARTA</strong>, Saya secara sadar menyetujui pelepasan tanggung jawab atas segala resiko yang terjadi.
                  </p>
                  <ol className="list-decimal list-inside space-y-3">
                    <li>Saya telah membaca dan menyetujui segala informasi terkait acara, peraturan serta informasi lainnya yang berkaitan dengan <strong>ULTRA24 JAKARTA</strong>.</li>
                    <li>Saya menyatakan dalam keadaan sehat secara fisik untuk mengikuti lomba <strong>ULTRA24 JAKARTA</strong>.</li>
                    <li>Saya menyatakan, segala bentuk cedera, penyakit atau cacat fisik yang terjadi sebelum dan/atau sesudah lomba berlangsung, dimana Saya mengakui cedera tersebut menjadi tanggung jawab Saya.</li>
                    <li>Saya menyadari bahwa lomba lari <strong>ULTRA24 JAKARTA</strong> berpotensi secara fisik menyebabkan saya cedera atau meninggal dunia selama lomba berlangsung. Jika Saya mengetahui adanya gangguan medis yang dapat mengganggu Saya saat lomba, maka Saya akan mengundurkan diri untuk mencegah bahaya cedera atau kondisi medis lainnya.</li>
                    <li>Saya menyatakan berpartisipasi pada lomba <strong>ULTRA24 JAKARTA</strong> dengan pertimbangan matang dan atas kemauan sendiri.</li>
                    <li>Saya setuju untuk mematuhi semua peraturan dan regulasi yang ditetapkan oleh panitia penyelenggara.</li>
                    <li>Saya memberikan izin kepada panitia untuk menggunakan foto dan video Saya selama acara untuk keperluan promosi.</li>
                  </ol>
                </div>
              </div>

              {/* Checkbox Agreement */}
              <div className="bg-slate-100 rounded-lg p-4 mb-6">
                <InputField
                  type="checkbox"
                  id="waiver-agreement"
                  label="Saya telah membaca dan menyetujui Pernyataan & Pelepasan Tanggung Jawab"
                  checked={isChecked}
                  onChange={(e) => setIsChecked(e.target.checked)}
                  labelColor={labelColor}
                  labelClassName="text-sm font-medium"
                  className="gap-3"
                />
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="text-slate-600 hover:text-slate-800 font-medium h-10 flex items-center"
                >
                  Back
                </button>
                <Button
                  onClick={handleNextStep}
                  disabled={!isChecked}
                  variant="primary"
                  className="px-8"
                >
                  Agree & Continue →
                </Button>
              </div>
            </>
          )}

          {/* Step 3: Submission */}
          {currentStep === 3 && (
            <>


              {/* Status Description */}
              <div className="mb-4 p-3 rounded-lg text-sm bg-slate-50 text-slate-600 border border-slate-200">
                {isUltraCategory
                  ? "Please review your data before submitting. Your registration will be verified by the admin before payment is enabled."
                  : "Please review your data before submitting. After submission, you can proceed directly to payment."}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-12 gap-4 mb-4">
                <div className="col-span-2 md:col-span-4">
                  <div className="text-xs text-slate-400 mb-1">Participant Name</div>
                  <div className="text-base font-semibold text-slate-800">
                    {formData.namaLengkap || "-"}
                  </div>
                </div>
              </div>

              {/* Info Grid - Aligned 12-column strategy */}
              <div className="grid grid-cols-2 md:grid-cols-12 gap-4 py-4 border-t border-slate-100">
                <div className="col-span-2 md:col-span-4">
                  <div className="text-xs text-slate-400 mb-1">Category</div>
                  <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap break-words">
                    {categoryName || title || "-"}
                  </div>
                </div>
                <div className="col-span-1 md:col-span-3">
                  <div className="text-xs text-slate-400 mb-1">Jersey Size</div>
                  <div className="text-sm font-medium text-slate-700">
                    {formData.sizeJersey || "-"}
                  </div>
                </div>
                <div className="col-span-1 md:col-span-3">
                  <div className="text-xs text-slate-400 mb-1">Registration Fee</div>
                  <div className="text-sm font-medium text-slate-700 md:whitespace-nowrap">
                    Rp {finalPrice.toLocaleString()}
                  </div>
                </div>
                <div className="col-span-2 md:col-span-2">
                  <div className="text-xs text-slate-400 mb-1">Status</div>
                  <div className="text-sm font-medium italic text-slate-500">
                    Ready to Submit
                  </div>
                </div>
              </div>

              {/* Note */}
              <div className="py-3 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  * Pembayaran akan dilakukan setelah pendaftaran diverifikasi oleh admin
                </p>
              </div>

              {/* Error/Success Messages */}
              {(errorMsg || successMsg) && (
                <div className={`p-4 rounded-lg mb-4 ${
                  errorMsg ? "bg-red-50 text-red-700 border border-red-200" : "bg-red-50 text-red-700 border border-red-200"
                }`}>
                  {errorMsg || successMsg}
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between items-center pt-6 border-t border-slate-200">
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="text-slate-600 hover:text-slate-800 font-medium"
                >
                  Back
                </button>
                {isSettled ? (
                  <Button
                    variant="secondary"
                    className="bg-slate-400 text-white opacity-50 cursor-not-allowed"
                    disabled
                  >
                    Already Registered
                  </Button>
                ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  variant="primary"
                >
                  {loading ? "Processing..." : "Submit Registration"}
                </Button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export const FormPendaftaran = (props) => (
  <GoogleReCaptchaProvider
    reCaptchaKey={import.meta.env.VITE_RECAPTCHA_SITE_KEY}
    scriptProps={{
      async: true,
      defer: true,
      appendTo: "head",
    }}
  >
    <FormPendaftaranContent {...props} />
  </GoogleReCaptchaProvider>
);
